import React from 'react';
import {
  withSystem,
  Plugins,
  withMeta,
  Component,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
  useExpressionEditorContext,
  type ExpressionEditorDispatch,
} from '@packages/systems/ExpressionEditor-utils';
import {
  TText,
  TRecord,
  TEnum,
  TTypeConstructor,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  qualify,
  typeName,
  iconName,
  pluginName,
} from '@packages/systems/wfdl-Qualified';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {
  Button,
  CollapseIcon,
  ExpandVerticalIcon,
  View,
} from '@packages/systems/spring';
import {stripAnn} from '@packages/utilities/annotated';
import {findFurthestAncestor} from '@packages/utilities/platform';
import {isNavbarWrapper} from '@packages/utilities/elementTypeUtils';
import {
  MENU_STATE_HIDE,
  MENU_STATE_SHOW,
  TEMP_NAVBAR_MENU_PATH,
} from '@packages/utilities/expression-path-constants';
import {
  selectSelectedElement,
  selectCurrentComponent,
} from '@packages/utilities/platform-selectors';
import {NAVBAR_MENU_OPEN_TOGGLED} from '@packages/systems/designer/actions';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

import {
  ContextualExpression,
  ContextualExpressions,
} from '@packages/utilities/contextual-expression';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {
  MiniToolbarButtons,
  PENDING_EDIT_TYPE,
  useMiniToolbarInstanceProxy,
} from '@packages/systems/designer/ui';

const {getIn, getEnum} = ContextualExpressions;

const CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];

const ANIMATION_PATH: ExpressionPath = [
  {in: 'Record', at: 'navbar'},
  {in: 'Record', at: 'animation'},
];

const DURATION_PATH: ExpressionPath = [
  {in: 'Record', at: 'navbar'},
  {in: 'Record', at: 'duration'},
];

const DOC_HEIGHT_PATH: ExpressionPath = [
  {in: 'Record', at: 'navbar'},
  {in: 'Record', at: 'docHeight'},
];

const NO_SCROLL_PATH: ExpressionPath = [
  {in: 'Record', at: 'navbar'},
  {in: 'Record', at: 'noScroll'},
];

const EASING_PATH: ExpressionPath = [
  {in: 'Record', at: 'navbar'},
  {in: 'Record', at: 'easing'},
];

const EASING2_PATH: ExpressionPath = [
  {in: 'Record', at: 'navbar'},
  {in: 'Record', at: 'easing2'},
];

const COLLAPSE_PATH: ExpressionPath = [
  {in: 'Record', at: 'navbar'},
  {in: 'Record', at: 'collapse'},
];

const Separator = emotionStyled('div')({
  width: '100%',
  height: tokens.space[2],
});

const animationOptions = {
  default: 'Drop Down',
  'over-right': 'Over Right',
  'over-left': 'Over Left',
} as const;

const navbarType = {
  type: withSystem(
    {
      private: true,
      defaultValue: 'wrapper',
    },
    TText
  ),
  easing: withSystem(
    {label: 'Easing open', defaultValue: 'ease', isLocalizable: true},
    TTypeConstructor(qualify(Plugins.name('Basic'), typeName('EasingsEnum')))
  ),
  easing2: withSystem(
    {label: 'Easing close', defaultValue: 'ease', isLocalizable: true},
    TTypeConstructor(qualify(Plugins.name('Basic'), typeName('EasingsEnum')))
  ),
  duration: withSystem(
    {
      label: 'Duration',
      defaultValue: 400,
      isLocalizable: true,
    },
    TTypeConstructor(qualify(Plugins.name('Basic'), typeName('MSNumber')))
  ),
  docHeight: withSystem(
    {
      labelVariant: 'hidden',
      label: 'Menu fills page height',
      defaultValue: false,
      isLocalizable: true,
    },
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
  noScroll: withSystem(
    {
      labelVariant: 'hidden',
      label: 'Disable scroll offset when fixed',
      defaultValue: false,
      isLocalizable: true,
    },
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
  animation: withMeta(
    {variant: 'menu', withoutLivePreview: true},
    withSystem(
      {label: 'Type', defaultValue: 'default', isLocalizable: true},
      TEnum(animationOptions)
    )
  ),
  collapse: withMeta(
    {
      icons: {
        all: qualify(pluginName('Navbar'), iconName('DeviceLaptopSmall')),
        medium: qualify(
          pluginName('Navbar'),
          iconName('DeviceTabletPortrait16')
        ),
        small: qualify(
          pluginName('Navbar'),
          iconName('DeviceMobileLandscape16')
        ),
        tiny: qualify(pluginName('Navbar'), iconName('DeviceMobilePortrait16')),
        none: qualify(pluginName('Navbar'), iconName('EyeInvisibleSmall')),
      },
    },
    withSystem(
      {
        labelVariant: 'hidden',
        defaultValue: 'medium',
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Navbar'), typeName('SliderNavbarCollapse'))
      )
    )
  ),
} as const;

const tempNavbarType = {
  menu: withSystem(
    {
      label: 'Menu',
      defaultValue: MENU_STATE_HIDE,
    },
    TEnum({show: 'Show', hide: 'Hide'})
  ),
} as const;

const childrenType = withSystem(
  {
    label: 'Add link',
    labelVariant: 'hidden',
  },
  TTypeConstructor(qualify(Plugins.name('Navbar'), typeName('AddNavbarLink')))
);

const NavbarWrapperDataFields = {
  tag: withSystem(
    {
      label: 'Tag',
      private: true,
    },
    TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
  ),
  temp: TRecord({
    navbar: TRecord(tempNavbarType),
  }),
  children: childrenType,
  navbar: TRecord(navbarType),
  search: withSystem(
    {private: true},
    TRecord({
      exclude: excludeSearchByDefaultType,
    })
  ),
} as const;

const selectStyleBlockStore = (state: DesignerStores) => state.StyleBlockStore;
const selectPendingEditAction = (state: DesignerStores) =>
  state.UiNodeStore.pendingEditAction;
const PENDING_EDIT_ACTION_CLEARED = 'PENDING_EDIT_ACTION_CLEARED';
const getCurrentNavbarWrapper = (
  state: DesignerStores
): ExpressionElement | undefined => {
  const selectedElement = selectSelectedElement(state);
  const rootElement = Component.getRender(selectCurrentComponent(state));

  if (!selectedElement) {
    return undefined;
  }

  return isNavbarWrapper(selectedElement)
    ? selectedElement
    : findFurthestAncestor(rootElement, selectedElement, isNavbarWrapper);
};

const NavbarWrapperRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
}) => {
  const styleBlockState = useDesignerSelector(selectStyleBlockStore);
  const pendingEditAction = useDesignerSelector(selectPendingEditAction);
  const currentNavbarWrapper = useDesignerSelector(getCurrentNavbarWrapper);
  const wfDispatch = useDispatch();
  const mediaQueryList = styleBlockState.getOrderedBreakpointIds();
  const eeContext = useExpressionEditorContext();
  const instanceProxyContext = useMiniToolbarInstanceProxy();
  const dispatchAt = extendExpressionEditorActionAtPath(dispatch);

  // a helper function to pass nested expression to ExpressionEditor
  const expressionEditorAtPath = React.useCallback(
    (path: ExpressionPath, newDispatch?: ExpressionEditorDispatch) => {
      const subExpr = getIn(expr, path);
      if (!subExpr)
        throw Error(
          `failed to get expression at path: ${JSON.stringify(path)}`
        );

      return (
        <ExpressionEditor
          expression={subExpr}
          dispatch={newDispatch || dispatchAt(path)}
        />
      );
    },
    [expr, dispatchAt]
  );

  const toggleNavbarMenu = React.useCallback(() => {
    if (!currentNavbarWrapper) {
      return;
    }

    // You may be asking, why are we calling this global flux dispatch?
    // That's a great question! The first reason is that opening the Navbar
    // menu requires a dispatch to `ExternalStore` in order to adjust the
    // MediaQuery based on the `data.navbar.collapse` value. The second
    // reason is there is a dependency in `designer-flux` when
    // opening the Navbar and we can't import them here directly because
    // some of it is client-only code (relying on window). This function
    // also handles adjusting `data.temp.navbar.menu` for us.
    wfDispatch({
      type: NAVBAR_MENU_OPEN_TOGGLED,
      payload: {
        navbarWrapperId: currentNavbarWrapper.id,
        mediaQueryList,
      },
    });
  }, [currentNavbarWrapper, mediaQueryList, wfDispatch]);

  // In page-level-inside-instance mode, clicking the compact button requests focus
  // first by setting a pending MiniToolbar action. Focus is applied by Flux state
  // updates, so the toggle must run after that transition completes.
  //
  // This effect listens for the matching pending action, clears it (one-shot), then
  // runs the Navbar toggle using the latest selector-derived state.
  React.useEffect(() => {
    if (
      pendingEditAction?.type === PENDING_EDIT_TYPE.MINI_TOOLBAR &&
      pendingEditAction.buttonType === MiniToolbarButtons.NAVBAR_TOGGLE
    ) {
      wfDispatch({type: PENDING_EDIT_ACTION_CLEARED});
      toggleNavbarMenu();
    }
  }, [pendingEditAction, wfDispatch, toggleNavbarMenu]);

  // At instance level, request focus via pending action. After focus completes,
  // pending action is observed above and toggleNavbarMenu runs.
  const onChangeMenu = () => {
    if (instanceProxyContext) {
      instanceProxyContext.dispatchPendingAction();
    } else {
      toggleNavbarMenu();
    }
  };

  const Menu = expressionEditorAtPath(TEMP_NAVBAR_MENU_PATH, onChangeMenu);
  const Animation = expressionEditorAtPath(ANIMATION_PATH);
  const Easing = expressionEditorAtPath(EASING_PATH);
  const Easing2 = expressionEditorAtPath(EASING2_PATH);
  const Duration = expressionEditorAtPath(DURATION_PATH);
  const DocHeight = expressionEditorAtPath(DOC_HEIGHT_PATH);
  const NoScroll = expressionEditorAtPath(NO_SCROLL_PATH);
  const Collapse = expressionEditorAtPath(COLLAPSE_PATH);
  const Children = expressionEditorAtPath(CHILDREN_PATH);

  if (eeContext === 'compact') {
    const menu = getIn(expr, TEMP_NAVBAR_MENU_PATH);
    if (!menu) return null;

    const menuValue = getEnum(stripAnn(menu));
    const isMenuOpen = menuValue === MENU_STATE_SHOW;
    const icon = isMenuOpen ? CollapseIcon : ExpandVerticalIcon;
    const label = isMenuOpen ? 'Hide menu' : 'Show menu';

    return (
      <View padding="$1">
        <Button
          onClick={onChangeMenu}
          prefix={icon}
          variant="ghost"
          density="compact"
        >
          {label}
        </Button>
      </View>
    );
  }

  return (
    <>
      {Menu}
      <Separator />
      {Animation}
      <Separator />
      {Easing}
      <Separator />
      {Easing2}
      <Separator />
      {Duration}
      <Separator />
      {DocHeight}
      {NoScroll}
      {Collapse}
      <Separator />
      {/* Add Navbar Link */}
      {Children}
    </>
  );
};

const NavbarWrapperType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields: NavbarWrapperDataFields,
  render: NavbarWrapperRender,
});

export default NavbarWrapperType;
